import React, { useEffect, useMemo, useCallback } from 'react'
import { snakeCase, isEqual } from 'lodash'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'

/* V2 components */
import { BarChart } from '@barracuda-internal/bds-core-v2/dist/Icons/Charts'
import { UserReportedEmails } from '@barracuda-internal/bds-core-v2/dist/Icons/Email'

import {
  ProfileMenu,
  BDSProvider,
  ProductSwitcher,
  ProductDataSubscriptionGroup
} from '@barracuda-internal/bds-core-v2'
import { useProductData } from '@barracuda-internal/bds-core-v2/dist/UnifiedComponents/ProductSwitcher/hooks/useProductData'
/* End V2 components */

import { FaviconEmail } from '@barracuda-internal/bds-core/dist/Logos/Favicons'

import {
  Apps,
  AutoAwesome,
  ExitToApp,
  MergeType,
  OpenInNew,
  RecentActors,
  Security,
  Settings,
  SupervisorAccount
} from '@barracuda-internal/bds-core/dist/Icons/Core'

import useAccessTokenLib from 'global/lib/accessToken/useAccessToken'
import { FEATURES, isMyFeatureOn } from 'global/lib/splitio/splitio'
import browserHistory from 'global/lib/routes/browserHistory'
import { config } from 'global/lib/config'
import * as analyticsLib from 'global/lib/analytics/analyticsService'
import useProductLib from 'global/lib/product/useProduct'
import useUserDataLib from 'global/lib/userData/useUserData'
import { useFormatMessage } from 'global/lib/localization'
import * as datetime from 'global/lib/datetime'
import { createUrlGenerator } from 'global/lib/routes/routesConfig'
import { isAfterDate } from 'global/lib/datetime'
import { logout, refreshToken } from 'global/redux/features/auth/authSlice'
import { setAccountSubscriptionData } from 'global/redux/features/account/accountSlice'
import { isPending } from 'global/redux/toolkit/api'
import { NavBarProps } from 'global/components/lib/navigation/NavigationBar'
import updateMockDemoUrlData from 'global/lib/mockProductData/mockProductData'

import routesConfig from 'fir/lib/routes/routesConfig'
import { useAppDispatch, useAppSelector } from 'fir/redux/toolkit/hooks'

type Jwt = {
  exp: number
  [key: string]: string | number
}

// TODO: Remove TOKEN_PLACEHOLDER in ticket BNFIR-4224
const TOKEN_PLACEHOLDER = 'TOKEN_PLACEHOLDER'
const FIVE_MINUTES = 5 * 60 * 1000
const BASE_FIR_I18N_KEY = 'fir.app.navbar'
const IR_PRODUCT_ID = config.PRODUCTS.IDS.FORENSICS.toString()

type GetInitials = {
  (name: string): string
}

export default function useFirNavbarLogic(): NavBarProps[] {
  const [accessTokenLib] = useAccessTokenLib()
  const formatMessage = useFormatMessage(BASE_FIR_I18N_KEY)
  const [productLib] = useProductLib()
  const [userDataLib] = useUserDataLib()
  const dispatch = useAppDispatch()
  const bccToken = Cookies.get(config.COOKIES.BCC_AT) || ''

  const {
    accessTokenId,
    accountSubscriptionData,
    activePath,
    bccAccount,
    defaultAccountBccId,
    isFirDemoUser,
    isRefreshTokenPending,
    splitStore,
    tenantId,
    userBccId,
    userEmail,
    userName
  } = useAppSelector(_stores => ({
    accessTokenId: _stores.accessToken.accessToken?.id || '',
    accountSubscriptionData: _stores.account.accountSubscriptionData,
    activePath: _stores.app.activePath.url || '',
    bccAccount: _stores.accessToken.bccAccount || '',
    defaultAccountBccId: _stores.user.data?.defaultAccountBccId || '',
    isFirDemoUser: _stores.user.isFirDemoUser,
    isRefreshTokenPending: isPending(_stores.auth.refreshTokenApiStatus),
    splitStore: _stores.splitio,
    tenantId: _stores.accessToken.accessToken?.tenantId || '',
    userBccId: _stores.user.data.bccUserId,
    userEmail: _stores.user.data.email,
    userName: _stores.user.data.displayName || ''
  }))

  const apiToken = bccToken || TOKEN_PLACEHOLDER

  const { data } = useProductData({
    userId: userBccId,
    accountId: bccAccount || defaultAccountBccId,
    apiToken,
    hostname: config.productDataServiceUrl
  })

  // BCC token has 1 hour expiration time, we need to get a new token when old token is expired
  useEffect(() => {
    // demo account and impersonation don't go through login flow curretnly, so skip the token check for them and use the hardcoded IP/ETS
    if (isFirDemoUser || userDataLib.isImpersonationMode()) {
      return
    }

    if (apiToken === TOKEN_PLACEHOLDER && !isRefreshTokenPending) {
      dispatch(refreshToken())

      return
    }

    if (bccToken) {
      const decodedToken = jwtDecode(bccToken) as Jwt
      const tokenExpireTime = new Date(decodedToken.exp * 1000 - FIVE_MINUTES) // Set the token expiration time to 5 minutes earlier, allowing us to get a new token when there are about 5 minutes remaining before it expires.
      const isTokenExpired = isAfterDate({ initialDate: new Date(), subtractedDate: tokenExpireTime })

      if (isTokenExpired) {
        dispatch(refreshToken())
      }
    }
  }, [apiToken, bccToken, dispatch, isFirDemoUser, isRefreshTokenPending, userDataLib])

  useEffect(() => {
    if (isFirDemoUser) {
      updateMockDemoUrlData(accessTokenId, dispatch)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isFirDemoUser && data?.subscriptionGroups && !isEqual(data.subscriptionGroups, accountSubscriptionData)) {
      const etsATs = userDataLib.getEtsAccessTokensByTenantId(tenantId)
      const mostRecentEtsAT = etsATs.length >= 1 ? etsATs[etsATs.length - 1].id : ''

      // Loop through each subscription group and product, update URL with access token if product ID is IP, IR, or ETS
      // Once we globalize the Unified Nav this function will only need to be added to one place, for now we will add it to all products
      data?.subscriptionGroups.forEach(subscriptionGroup =>
        subscriptionGroup.subscriptions.forEach(subscription => {
          switch (subscription.product.id) {
            case config.PRODUCTS.IDS.ETS.toString():
              // eslint-disable-next-line no-param-reassign
              subscription.product.appUrls.main = mostRecentEtsAT
                ? `${config.domains.ets}/report/${mostRecentEtsAT}`
                : subscription.product.appUrls.main
              break
            case config.PRODUCTS.IDS.FORENSICS.toString():
              // eslint-disable-next-line no-param-reassign
              subscription.product.appUrls.main = accessTokenId
                ? `${config.domains.forensics}/report/${accessTokenId}/remediation`
                : subscription.product.appUrls.main
              break
            case config.PRODUCTS.IDS.DFP.toString():
            case config.PRODUCTS.IDS.SENTINEL.toString():
              // eslint-disable-next-line no-param-reassign
              subscription.product.appUrls.main = accessTokenId
                ? `${config.domains.sentinel}/report/${accessTokenId}/dashboard`
                : subscription.product.appUrls.main
              break
            default:
              break
          }
        })
      )

      dispatch(setAccountSubscriptionData(data.subscriptionGroups))
    }
  }, [dispatch, userDataLib, tenantId, accessTokenId, isFirDemoUser, data.subscriptionGroups, accountSubscriptionData])

  const isRouteDisabled = useMemo(
    () => activePath === routesConfig.START_TRIAL.path || activePath.includes('/missing-requirements/'),
    [activePath]
  )

  const isAdmin = useMemo(() => userDataLib.isUserAdmin(accessTokenId), [accessTokenId, userDataLib])

  const isLicenseComplianceOn = useMemo(() => isMyFeatureOn(splitStore, FEATURES.IP_LICENSING_COMPLIANCE, bccAccount), [
    bccAccount,
    splitStore
  ])

  const isLicenseVisible = useMemo(() => {
    return (
      !userDataLib.isMspManagedAccount(accessTokenId) &&
      isLicenseComplianceOn &&
      accessTokenLib.hasForensicsEntitlement(accessTokenId)
    )
  }, [accessTokenId, accessTokenLib, isLicenseComplianceOn, userDataLib])

  const getInitials: GetInitials = useCallback((name: string) => {
    const names = name.split(' ')
    const initials = names.map((n: string) => n[0]).join('')
    return initials.toUpperCase()
  }, [])

  const onNavigate = useCallback((path: string) => {
    browserHistory.push(path)
  }, [])

  const generatePath = useCallback((path: string) => {
    const createUrl = createUrlGenerator(path)
    // We don't need to pass params for navbar since the function createUrlGenerator function already passed the accessTokenId param,
    // for other params like reportId, it's been handled inside the component itself.
    return `${createUrl({})}`
  }, [])

  const onGoToLicense = useCallback(() => {
    analyticsLib.trackAppEvent(analyticsLib.EVENTS.NAVBAR_REVIEW_LICENSES)
    routesConfig.LICENSE.goto({
      accessToken: accessTokenId
    })
  }, [accessTokenId])

  // start logout
  const onLogout = useCallback(() => {
    dispatch(logout(true))
  }, [dispatch])

  const serialNumber = useMemo(() => productLib.getForensicsSerialNumberForAccessToken(accessTokenId), [
    accessTokenId,
    productLib
  ])

  const expiration = useMemo(() => productLib.getForensicsSerialExpiryForAccessToken(accessTokenId), [
    accessTokenId,
    productLib
  ])

  return useMemo(
    () => [
      {
        currentPath: activePath,
        onNavigate,
        options: [
          {
            name: formatMessage('options.apps'),
            icon: <Apps />,
            menu: (
              // TODO: remove BDSProvider once we remove MuiThemeProvider in App.tsx
              <BDSProvider useBdsTheme>
                <ProductSwitcher
                  currentProductId={IR_PRODUCT_ID}
                  subscriptionGroups={accountSubscriptionData as ProductDataSubscriptionGroup[]}
                />
              </BDSProvider>
            )
          },
          {
            name: formatMessage('options.profile'),
            initials: getInitials(userName),
            menu: (
              <ProfileMenu
                initials={getInitials(userName)}
                name={userName}
                email={userEmail}
                menuItems={[
                  ...(isAdmin
                    ? [
                        {
                          name: formatMessage('options.profile_menu.manage_account'),
                          onClick: (): void => {
                            window.open(`${config.bccAccountUserUrl}/#user=${userBccId}`, '_blank')
                          },
                          icon: <SupervisorAccount />,
                          endIcon: <OpenInNew />
                        }
                      ]
                    : []),
                  ...(isLicenseVisible
                    ? [
                        {
                          name: formatMessage('options.profile_menu.review_licenses'),
                          onClick: onGoToLicense,
                          icon: <RecentActors />
                        }
                      ]
                    : []),

                  {
                    name: formatMessage('options.profile_menu.log_out'),
                    onClick: onLogout,
                    icon: <ExitToApp />
                  }
                ]}
                footer={
                  serialNumber || expiration
                    ? {
                        ...(serialNumber
                          ? {
                              serial: serialNumber,
                              serialText: formatMessage('serial_text')
                            }
                          : {}),
                        ...(expiration
                          ? {
                              expirationDate: datetime.formatDate(expiration, config.DATETIME.DEFAULT_DATE_FORMAT),
                              expirationDateText: formatMessage('expiration_date_text')
                            }
                          : {})
                      }
                    : undefined
                }
              />
            )
          }
        ],
        routes: [
          {
            path: generatePath(routesConfig.REMEDIATION.path),
            icon: <Security />,
            name: formatMessage(`${snakeCase(routesConfig.REMEDIATION.id)}`),
            disabled: isRouteDisabled
          },
          {
            path: generatePath(routesConfig.USER_REPORTED.path),
            icon: <UserReportedEmails />,
            name: formatMessage(`${snakeCase(routesConfig.USER_REPORTED.id)}`),
            disabled: isRouteDisabled
          },
          ...(productLib.getForensicsSerialBundleForAccessToken(accessTokenId) === config.BUNDLES.BUNDLE1
            ? []
            : [
                {
                  path: generatePath(routesConfig.INSIGHTS.path),
                  icon: <AutoAwesome />,
                  name: formatMessage(`${snakeCase(routesConfig.INSIGHTS_AUTOMATED.id)}`),
                  disabled: isRouteDisabled
                },
                {
                  path: generatePath(routesConfig.AUTOMATED_WORKFLOWS.path),
                  icon: <MergeType />,
                  name: formatMessage(`${snakeCase(routesConfig.AUTOMATED_WORKFLOWS.id)}`),
                  disabled: isRouteDisabled
                },
                {
                  path: generatePath(routesConfig.UNIFIED_REPORTING_ROOT.path),
                  icon: <BarChart />,
                  name: formatMessage(`${snakeCase(routesConfig.UNIFIED_REPORTING_ROOT.id)}`),
                  disabled: isRouteDisabled,
                  children: [
                    {
                      name: formatMessage(`${snakeCase(routesConfig.REPORT_LIST.id)}`),
                      disabled: isRouteDisabled,
                      path: generatePath(routesConfig.REPORT_LIST.path)
                    },
                    {
                      name: formatMessage(`${snakeCase(routesConfig.SCHEDULED_REPORTS.id)}`),
                      disabled: isRouteDisabled,
                      path: generatePath(routesConfig.SCHEDULED_REPORTS.path)
                    }
                  ]
                }
              ]),
          {
            path: generatePath(routesConfig.SETTINGS.path),
            icon: <Settings />,
            name: formatMessage(`${snakeCase(routesConfig.SETTINGS.id)}`),
            disabled: isRouteDisabled
          }
        ],
        logo: <FaviconEmail viewBox="0 0 56 56" />,
        globalNavigation: data.globalNavigation || []
      }
    ],
    [
      accessTokenId,
      accountSubscriptionData,
      activePath,
      data.globalNavigation,
      expiration,
      formatMessage,
      generatePath,
      getInitials,
      isAdmin,
      isLicenseVisible,
      isRouteDisabled,
      onGoToLicense,
      onLogout,
      onNavigate,
      productLib,
      serialNumber,
      userBccId,
      userEmail,
      userName
    ]
  )
}
